import React, { useEffect, useMemo, useState } from 'react';
import { Edit, Home, Plus, Star, Trash2, X } from 'lucide-react';
import { ConfirmDialog } from '../../../shared/components/ConfirmDialog';
import {
  useAtualizarEndereco,
  useCriarEndereco,
  useDefinirEnderecoPrincipal,
  useEnderecos,
  useExcluirEndereco,
} from '../hooks';
import { useEstados, useMunicipios } from '../../geo/hooks';
import { maskCEP } from '../../../shared/utils/validators';
import { getFriendlyErrorMessage } from '../../../shared/lib/errors';
import { TextInput } from '../../../shared/components/TextInput';
import { AutocompleteSelect } from '../../../shared/components/AutocompleteSelect';
import type {
  CreateEnderecoPayload,
  Endereco,
  TipoEndereco,
  UpdateEnderecoPayload,
} from '../api';

interface Props {
  clientId: string;
}

const TIPO_LABEL: Record<TipoEndereco, string> = {
  matriz: 'Matriz',
  filial: 'Filial',
  unidade_operacional: 'Unidade Operacional',
  outros: 'Outros',
};

/**
 * CRUD de endereços do cliente (Fase 2 — RN-COM-009). Mostra tabela +
 * botão de novo + ações de editar/excluir por linha; o backend garante
 * exatamente 1 principal ativo e promove o próximo automaticamente ao
 * deletar o principal.
 */
export const ClienteEnderecos: React.FC<Props> = ({ clientId }) => {
  const { data, isLoading, isError, error } = useEnderecos(clientId);
  const criar = useCriarEndereco(clientId);
  const atualizar = useAtualizarEndereco(clientId);
  const excluir = useExcluirEndereco(clientId);
  const definirPrincipal = useDefinirEnderecoPrincipal(clientId);
  const [modo, setModo] = useState<{ tipo: 'create' } | { tipo: 'edit'; endereco: Endereco } | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<Endereco | null>(null);
  const [feedback, setFeedback] = useState<{ kind: 'ok' | 'err'; msg: string } | null>(null);

  const enderecos: Endereco[] = data?.data ?? [];

  const showFeedback = (kind: 'ok' | 'err', msg: string) => {
    setFeedback({ kind, msg });
    setTimeout(() => setFeedback(null), 4000);
  };

  const extractError = (err: unknown): string => getFriendlyErrorMessage(err, 'Falha na operação.');

  const handleCreate = async (payload: CreateEnderecoPayload) => {
    try {
      await criar.mutateAsync(payload);
      showFeedback('ok', 'Endereço adicionado.');
      setModo(null);
    } catch (err) {
      showFeedback('err', extractError(err));
    }
  };

  const handleUpdate = async (addressId: string, payload: UpdateEnderecoPayload) => {
    try {
      await atualizar.mutateAsync({ addressId, data: payload });
      showFeedback('ok', 'Endereço atualizado.');
      setModo(null);
    } catch (err) {
      showFeedback('err', extractError(err));
    }
  };

  const handleSetPrincipal = async (addressId: string) => {
    try {
      await definirPrincipal.mutateAsync(addressId);
      showFeedback('ok', 'Endereço principal atualizado.');
    } catch (err) {
      showFeedback('err', extractError(err));
    }
  };

  const performDelete = async () => {
    if (!confirmDelete) return;
    try {
      await excluir.mutateAsync(confirmDelete.id);
      showFeedback('ok', 'Endereço removido.');
      setConfirmDelete(null);
    } catch (err) {
      showFeedback('err', extractError(err));
      setConfirmDelete(null);
    }
  };

  return (
    <div className="space-y-3" data-pending-anchor="enderecos">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
          <Home size={18} /> Endereços
        </h3>
        <button
          type="button"
          onClick={() => setModo({ tipo: 'create' })}
          className="inline-flex items-center gap-2 rounded-md bg-blue-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-blue-700"
        >
          <Plus size={16} /> Novo endereço
        </button>
      </div>

      {feedback && (
        <div className={`rounded-md border p-3 text-sm ${
          feedback.kind === 'ok'
            ? 'border-green-200 bg-green-50 text-green-700'
            : 'border-red-200 bg-red-50 text-red-700'
        }`}>
          {feedback.msg}
        </div>
      )}

      {isLoading ? (
        <div className="text-sm text-gray-500">Carregando endereços…</div>
      ) : isError ? (
        <div className="text-sm text-red-700">Erro: {(error as Error)?.message}</div>
      ) : enderecos.length === 0 ? (
        <div className="rounded-md border border-dashed border-gray-300 bg-gray-50 p-6 text-center text-sm text-gray-500">
          Nenhum endereço cadastrado. Ativação exige ao menos 1 endereço principal (RN-COM-009).
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Tipo</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Apelido</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Endereço</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Cidade/UF</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">CEP</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Principal</th>
                <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Ações</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {enderecos.map((e) => (
                <tr key={e.id} className={e.principal ? 'bg-yellow-50/50' : ''}>
                  <td className="px-4 py-2 text-sm text-gray-900">{TIPO_LABEL[e.tipo]}</td>
                  <td className="px-4 py-2 text-sm text-gray-700">{e.apelido ?? '—'}</td>
                  <td className="px-4 py-2 text-sm text-gray-700">
                    {e.logradouro}
                    {e.numero ? `, ${e.numero}` : ''}
                    {e.complemento ? ` — ${e.complemento}` : ''}
                    {e.bairro ? ` · ${e.bairro}` : ''}
                  </td>
                  <td className="px-4 py-2 text-sm text-gray-700">{e.cidade}/{e.uf}</td>
                  <td className="px-4 py-2 text-sm text-gray-700 font-mono">
                    {e.cep.replace(/(\d{5})(\d{3})/, '$1-$2')}
                  </td>
                  <td className="px-4 py-2 text-sm">
                    {e.principal ? (
                      <span className="inline-flex items-center gap-1 rounded-full bg-yellow-100 px-2 py-0.5 text-xs font-medium text-yellow-800">
                        <Star size={12} /> Principal
                      </span>
                    ) : (
                      <button
                        type="button"
                        onClick={() => handleSetPrincipal(e.id)}
                        disabled={definirPrincipal.isPending}
                        className="text-xs text-blue-600 hover:text-blue-800 hover:underline disabled:opacity-50"
                      >
                        Tornar principal
                      </button>
                    )}
                  </td>
                  <td className="px-4 py-2 text-right">
                    <div className="flex justify-end gap-2">
                      <button
                        type="button"
                        onClick={() => setModo({ tipo: 'edit', endereco: e })}
                        className="p-1 text-blue-600 hover:text-blue-900 hover:bg-blue-50 rounded transition-colors"
                        title="Editar endereço"
                        aria-label={`Editar endereço ${e.tipo}`}
                      >
                        <Edit size={16} />
                      </button>
                      <button
                        type="button"
                        onClick={() => setConfirmDelete(e)}
                        disabled={excluir.isPending}
                        className="p-1 text-red-600 hover:text-red-900 hover:bg-red-50 rounded transition-colors disabled:opacity-50"
                        title="Excluir endereço"
                        aria-label={`Excluir endereço ${e.tipo}`}
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {modo && (
        <EnderecoFormModal
          mode={modo.tipo}
          initial={modo.tipo === 'edit' ? modo.endereco : undefined}
          submitting={criar.isPending || atualizar.isPending}
          onCancel={() => setModo(null)}
          onSubmit={async (payload) => {
            if (modo.tipo === 'edit') {
              // Em edit, ignora `principal` (endpoint dedicado no set-principal cuida).
              const { principal: _p, ...rest } = payload;
              await handleUpdate(modo.endereco.id, rest);
            } else {
              await handleCreate(payload);
            }
          }}
        />
      )}

      <ConfirmDialog
        open={!!confirmDelete}
        title="Excluir endereço?"
        message={
          confirmDelete
            ? `${TIPO_LABEL[confirmDelete.tipo]}${confirmDelete.apelido ? ` — ${confirmDelete.apelido}` : ''}\n${confirmDelete.logradouro}${
                confirmDelete.numero ? `, ${confirmDelete.numero}` : ''
              } — ${confirmDelete.cidade}/${confirmDelete.uf}${
                confirmDelete.principal
                  ? '\n\nEste é o endereço PRINCIPAL. Ao excluir, o próximo endereço mais antigo será promovido automaticamente.'
                  : ''
              }`
            : ''
        }
        confirmLabel="Excluir"
        danger
        busy={excluir.isPending}
        onConfirm={performDelete}
        onCancel={() => setConfirmDelete(null)}
      />
    </div>
  );
};

interface EnderecoFormModalProps {
  mode: 'create' | 'edit';
  initial?: Endereco;
  submitting: boolean;
  onCancel: () => void;
  onSubmit: (payload: CreateEnderecoPayload) => Promise<void> | void;
}

const EnderecoFormModal: React.FC<EnderecoFormModalProps> = ({
  mode,
  initial,
  submitting,
  onCancel,
  onSubmit,
}) => {
  const [tipo, setTipo] = useState<TipoEndereco>('matriz');
  const [apelido, setApelido] = useState('');
  const [logradouro, setLogradouro] = useState('');
  const [numero, setNumero] = useState('');
  const [complemento, setComplemento] = useState('');
  const [bairro, setBairro] = useState('');
  const [cidade, setCidade] = useState('');
  const [uf, setUf] = useState('');
  const [cep, setCep] = useState('');
  const [principal, setPrincipal] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Estados/municípios do IBGE (com fallback DB no backend).
  const estadosQuery = useEstados();
  const municipiosQuery = useMunicipios(uf);

  // Hidrata quando abrir em modo edit.
  useEffect(() => {
    if (initial) {
      setTipo(initial.tipo);
      setApelido(initial.apelido ?? '');
      setLogradouro(initial.logradouro);
      setNumero(initial.numero ?? '');
      setComplemento(initial.complemento ?? '');
      setBairro(initial.bairro ?? '');
      setCidade(initial.cidade);
      setUf(initial.uf);
      setCep(maskCEP(initial.cep));
      setPrincipal(initial.principal);
      setError(null);
    }
  }, [initial]);

  // Quando o usuário troca a UF, limpamos a cidade selecionada — a lista
  // vai mudar. Em modo edit, se a UF for a mesma que veio do initial,
  // preservamos a cidade original (evita apagar dado válido do usuário).
  const handleUfChange = (novaUf: string) => {
    setUf(novaUf);
    if (novaUf !== initial?.uf) setCidade('');
  };

  // Ordena municípios só uma vez por lista carregada.
  const municipiosOrdenados = useMemo(
    () => (municipiosQuery.data ?? []).slice().sort((a, b) => a.nome.localeCompare(b.nome, 'pt-BR')),
    [municipiosQuery.data],
  );

  const isEdit = mode === 'edit';

  const handleSubmit = async (evt: React.FormEvent) => {
    evt.preventDefault();
    setError(null);
    if (!logradouro.trim() || !cidade.trim() || !uf.trim() || !cep.trim()) {
      setError('Logradouro, cidade, UF e CEP são obrigatórios.');
      return;
    }
    const cepDigits = cep.replace(/\D/g, '');
    if (cepDigits.length !== 8) {
      setError('CEP deve ter 8 dígitos.');
      return;
    }
    const payload: CreateEnderecoPayload = {
      tipo,
      logradouro: logradouro.trim(),
      cidade: cidade.trim(),
      uf: uf.toUpperCase().slice(0, 2),
      cep: cepDigits,
    };
    if (apelido.trim()) payload.apelido = apelido.trim();
    if (numero.trim()) payload.numero = numero.trim();
    if (complemento.trim()) payload.complemento = complemento.trim();
    if (bairro.trim()) payload.bairro = bairro.trim();
    // `principal` só é enviado no create; no edit, o usuário usa
    // "Tornar principal" na listagem (endpoint dedicado).
    if (!isEdit && principal) payload.principal = true;
    await onSubmit(payload);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4">
      <div className="w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-lg bg-white shadow-xl">
        <div className="flex items-center justify-between border-b border-gray-200 p-4">
          <h3 className="text-xl font-bold text-gray-900">
            {isEdit ? 'Editar endereço' : 'Novo endereço'}
          </h3>
          <button type="button" onClick={onCancel} aria-label="Fechar" className="text-gray-400 hover:text-gray-600">
            <X size={20} />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="space-y-3 p-6">
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            <AutocompleteSelect
              label="Tipo"
              options={[
                { value: 'matriz', label: 'Matriz' },
                { value: 'filial', label: 'Filial' },
                { value: 'unidade_operacional', label: 'Unidade Operacional' },
                { value: 'outros', label: 'Outros' },
              ]}
              value={tipo}
              onChange={(v) => setTipo(v as TipoEndereco)}
            />
            <TextInput
              label="Apelido"
              value={apelido}
              onChange={(e) => setApelido(e.target.value)}
            />
          </div>

          <TextInput
            label="Logradouro *"
            value={logradouro}
            onChange={(e) => setLogradouro(e.target.value)}
          />

          <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
            <TextInput
              label="Número"
              value={numero}
              onChange={(e) => setNumero(e.target.value)}
            />
            <div className="md:col-span-2">
              <TextInput
                label="Complemento"
                value={complemento}
                onChange={(e) => setComplemento(e.target.value)}
              />
            </div>
          </div>

          <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
            <TextInput
              label="Bairro"
              value={bairro}
              onChange={(e) => setBairro(e.target.value)}
            />
            <div>
              <AutocompleteSelect
                label="UF *"
                options={[
                  {
                    value: '',
                    label: estadosQuery.isLoading
                      ? 'Carregando…'
                      : estadosQuery.isError
                        ? 'Indisponível'
                        : 'Selecione',
                  },
                  ...(estadosQuery.data ?? []).map((e) => ({
                    value: e.uf,
                    label: `${e.uf} — ${e.nome}`,
                  })),
                ]}
                value={uf}
                onChange={(v) => handleUfChange(v)}
                disabled={estadosQuery.isLoading || estadosQuery.isError}
              />
              {estadosQuery.isError && (
                <span className="mt-1 block text-xs text-red-600">
                  Não foi possível carregar as UFs. Tente novamente em instantes.
                </span>
              )}
            </div>
            <div>
              <AutocompleteSelect
                label="Cidade *"
                options={[
                  {
                    value: '',
                    label: !uf
                      ? 'Selecione a UF primeiro'
                      : municipiosQuery.isLoading
                        ? 'Carregando…'
                        : municipiosQuery.isError
                          ? 'Indisponível'
                          : 'Selecione',
                  },
                  // Em modo edit, se a cidade original não estiver na
                  // lista carregada (raro — nome divergindo do IBGE),
                  // injetamos a original pra não sumir o valor.
                  ...(cidade && !municipiosOrdenados.some((m) => m.nome === cidade)
                    ? [{ value: cidade, label: cidade }]
                    : []),
                  ...municipiosOrdenados.map((m) => ({
                    value: m.nome,
                    label: m.nome,
                  })),
                ]}
                value={cidade}
                onChange={(v) => setCidade(v)}
                disabled={!uf || municipiosQuery.isLoading || municipiosQuery.isError}
              />
              {municipiosQuery.isError && (
                <span className="mt-1 block text-xs text-red-600">
                  Não foi possível carregar as cidades.
                </span>
              )}
            </div>
          </div>

          <TextInput
            label="CEP *"
            value={cep}
            onChange={(e) => setCep(maskCEP(e.target.value))}
            inputMode="numeric"
            maxLength={9}
          />


          {!isEdit && (
            <label className="flex items-center gap-2 text-sm text-gray-700">
              <input type="checkbox" checked={principal} onChange={(e) => setPrincipal(e.target.checked)} />
              Definir como endereço principal
            </label>
          )}
          {isEdit && initial?.principal && (
            <div className="rounded-md border border-yellow-200 bg-yellow-50 p-2 text-xs text-yellow-800">
              Este é o endereço principal. Para trocar, use "Tornar principal" em outro endereço da lista.
            </div>
          )}

          {error && <div className="rounded-md border border-red-200 bg-red-50 p-2 text-xs text-red-700">{error}</div>}

          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onCancel} className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm text-gray-700 hover:bg-gray-50">
              Cancelar
            </button>
            <button type="submit" disabled={submitting} className="rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50">
              {submitting ? 'Salvando…' : isEdit ? 'Salvar alterações' : 'Adicionar endereço'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
